import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import type { FatiaDoRelatorio } from '@central/contracts';
import {
  Carregando,
  Contagem,
  Falhou,
  Numero,
  Painel,
  ResumoDoRecorte,
  type ColunaDeTabela,
} from '@/features/relatorios/componentes';
import { useFiltro } from '@/features/relatorios/filtros';
import { GraficoDeBarras } from '@/features/relatorios/graficos';
import { chavesDeRelatorio, obterFcr } from '@/features/relatorios/relatorios.api';

/**
 * FCR — First Contact Resolution.
 *
 * Mede a proporção de chamados resolvidos sem nenhuma reabertura.
 * Um FCR alto significa que os problemas são realmente resolvidos, não
 * encerrados administrativamente.
 */

const COLUNAS: ReadonlyArray<ColunaDeTabela<FatiaDoRelatorio>> = [
  { cabecalho: 'Equipe', celula: (l) => l.rotulo, csv: (l) => l.rotulo },
  {
    cabecalho: 'Na primeira',
    alinharADireita: true,
    celula: (l) => l.total.toLocaleString('pt-BR'),
    csv: (l) => l.total,
  },
];

export function FcrPage() {
  const { consulta } = useFiltro();
  const relatorio = useQuery({
    queryKey: chavesDeRelatorio.de('fcr', consulta),
    queryFn: () => obterFcr(consulta),
  });

  if (relatorio.isPending) return <Carregando />;
  if (relatorio.isError) {
    return <Falhou erro={relatorio.error} aoTentar={() => void relatorio.refetch()} />;
  }

  const dados = relatorio.data;
  const fcr = dados.fcr.estado === 'OK' ? dados.fcr.valor : null;

  return (
    <div className="animate-slide-up space-y-6">
      <ResumoDoRecorte recorte={dados.recorte} />

      <div className="grid gap-3 sm:grid-cols-3">
        <Numero
          rotulo="FCR"
          indicador={dados.fcr}
          formatar={(valor) => `${(valor * 100).toFixed(1).replace('.', ',')}%`}
          tom={fcr === null ? 'neutro' : fcr >= 0.7 ? 'bom' : fcr >= 0.5 ? 'atencao' : 'ruim'}
        />
        <Contagem rotulo="Resolvidos no período" valor={dados.encerrados} />
        <Contagem
          rotulo="Na primeira tentativa"
          valor={dados.naPrimeira}
          tom={fcr === null ? 'neutro' : fcr >= 0.7 ? 'bom' : 'atencao'}
        />
      </div>

      <p className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden />
        <span>
          <span className="font-medium">Como é apurado:</span> {dados.metodo}
        </span>
      </p>

      <Painel
        titulo="FCR por equipe — resolvidos na primeira tentativa"
        descricao="Uma equipe com FCR baixo costuma ter problemas de diagnóstico ou de escopo — encerra o que não resolveu."
        indicador={dados.porEquipe}
        grafico={(linhas) => (
          <GraficoDeBarras
            dados={linhas.map((l) => ({ rotulo: l.rotulo, total: l.total }))}
            nome="Na primeira"
          />
        )}
        colunas={() => COLUNAS}
        chaveDe={(linha) => linha.chave}
        nome={['relatorio', 'fcr', 'por-equipe']}
        recorte={dados.recorte}
      />
    </div>
  );
}
